import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { FiEdit2, FiPlus, FiShoppingBag } from "react-icons/fi";
import { useCart } from "../../context/CartContext";
import { useCartDrawer } from "../../context/CartDrawerContext";
import { useToast } from "../../context/ToastContext";
import { formatLek } from "../../Utils/currency";
import RatingStars from "../../ui/RatingStars";
import * as P from "./Products.styled";

const stockState = (stock) => {
  if (stock <= 0) return "out";
  if (stock <= 5) return "low";
  return "in";
};

const ProductCardRedesigned = ({
  product,
  variant = "grid",
  index = 0,
  onView,
  onEdit,
  isFarmer,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addItemToCart } = useCart();
  const { peekDrawer } = useCartDrawer();
  const { success: toastSuccess } = useToast();
  const reduceMotion = useReducedMotion();

  const badges = Array.isArray(product.badges) ? product.badges : [];
  const hasStock = Number(product.stock || 0);
  const state = stockState(hasStock);
  const isOutOfStock = state === "out";
  const isLowStock = state === "low";
  const isPopular = Number(product.orderCount || 0) >= 8;

  const stockLabel =
    state === "out"
      ? t("products.out_of_stock", { defaultValue: "Out of stock" })
      : state === "low"
      ? t("products.low_stock", {
          defaultValue: "Only {{count}} left",
          count: hasStock,
        })
      : t("products.in_stock", { defaultValue: "In stock" });

  const handleQuickAdd = (event) => {
    event.stopPropagation();
    if (isOutOfStock) return;
    const added = addItemToCart({
      id: product.id,
      farmId: product.farmId,
      farmName: product.farmName || "",
      name: product.name,
      price: Number(product.price || 0),
      image: product.image,
      unit: product.unit,
      description: product.description,
    });
    if (added) {
      peekDrawer();
      toastSuccess(
        `${product.name} ${t("cart.added", { defaultValue: "added to cart" })}`
      );
    }
  };

  const handleCta = (event) => {
    event.stopPropagation();
    if (isOutOfStock) return;
    handleQuickAdd(event);
  };

  const handleEditClick = (event) => {
    event.stopPropagation();
    onEdit?.(product);
  };

  const handleCardClick = () => {
    if (onView) onView();
    else navigate(`/products/${product.id}`);
  };

  return (
    <P.Card
      as={motion.article}
      $variant={variant}
      initial={{ opacity: 0, y: reduceMotion ? 0 : 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: reduceMotion ? 0 : 0.4,
        delay: reduceMotion ? 0 : Math.min(index * 0.04, 0.24),
        ease: [0.22, 1, 0.36, 1],
      }}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleCardClick();
        }
      }}
    >
      <P.CardMedia $variant={variant}>
        <P.CardImage $src={product.image} aria-hidden="true" />
        <P.BadgeStack>
          {isLowStock ? (
            <P.UrgencyBadge $tone="warning">
              {t("products.urgency.only_left", {
                defaultValue: "Only {{count}} left",
                count: hasStock,
              })}
            </P.UrgencyBadge>
          ) : null}
          {isPopular && !isLowStock ? (
            <P.UrgencyBadge $tone="info">
              {t("products.urgency.popular", { defaultValue: "Popular" })}
            </P.UrgencyBadge>
          ) : null}
        </P.BadgeStack>
        <P.QuickAddBtn
          type="button"
          onClick={handleQuickAdd}
          disabled={isOutOfStock}
          $disabled={isOutOfStock}
          aria-label={
            isOutOfStock
              ? t("products.out_of_stock", { defaultValue: "Out of stock" })
              : t("products.add_to_cart", {
                  defaultValue: "Add {{name}} to cart",
                  name: product.name,
                })
          }
        >
          <FiPlus aria-hidden="true" />
        </P.QuickAddBtn>
      </P.CardMedia>

      <P.CardBody>
        <P.CardTitleRow>
          <P.CardTitle>{product.name}</P.CardTitle>
          <P.Price>
            {formatLek(product.price)}
            {product.unit ? <small>/ {product.unit}</small> : null}
          </P.Price>
        </P.CardTitleRow>

        {Number(product.rating) > 0 ? (
          <RatingStars
            rating={product.rating}
            count={product.reviewCount}
            size="0.8rem"
          />
        ) : null}

        {product.description ? (
          <P.CardDescription>{product.description}</P.CardDescription>
        ) : null}

        {badges.length > 0 || product.season ? (
          <P.TagRow>
            {badges.map((badge) => (
              <P.Tag key={badge} $tone="accent">
                {badge}
              </P.Tag>
            ))}
            {product.season ? (
              <P.Tag $tone="warning">{product.season}</P.Tag>
            ) : null}
          </P.TagRow>
        ) : null}

        <P.CardFooter>
          <P.StockIndicator $state={state}>{stockLabel}</P.StockIndicator>
          <div style={{ display: "inline-flex", gap: 6 }}>
            {isFarmer ? (
              <P.EditLinkBtn type="button" onClick={handleEditClick}>
                <FiEdit2 aria-hidden="true" size={12} style={{ marginRight: 4 }} />
                {t("products.edit", { defaultValue: "Edit" })}
              </P.EditLinkBtn>
            ) : null}
            <P.CardCta
              type="button"
              onClick={handleCta}
              disabled={isOutOfStock}
              $disabled={isOutOfStock}
            >
              <FiShoppingBag aria-hidden="true" size={14} />
              {isOutOfStock
                ? t("products.out_of_stock", { defaultValue: "Out of stock" })
                : t("products.add_to_cart", { defaultValue: "Add to cart" })}
            </P.CardCta>
          </div>
        </P.CardFooter>
      </P.CardBody>
    </P.Card>
  );
};

export default ProductCardRedesigned;
